import React, { useCallback, useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Truck, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo, LOGOS_HIDDEN } from './Logo';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
      setMobileAboutOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  const navLinkClass = 'text-stone-800 dark:text-white';
  const dropdownLinkClass = 'text-stone-600 dark:text-stone-300';
  // Category labels in the Shop Materials menu use the brand green in
  // BOTH light and dark modes — matches the visual treatment customers
  // already see for the green "LIVE", "Pay on delivery", and pill chips
  // throughout the site, so the Shop section reads as on-brand instead
  // of a muted secondary palette. Contractor pages override to
  // brand-orange to preserve the contractor-specific visual identity.
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-[#17A347] dark:text-[#22c95c]';
  const {
    totalItems,
    totalPrice,
    toggleCart
  } = useCart();
  const t = useT();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  // Navigate straight to a product's detail page from the Shop dropdown.
  // Previously this routed everything through the home-page Materials Hub
  // (/?cat=…&prod=…#materials), which meant clicking a specific product
  // from any non-home page dumped the customer at the hub instead of the
  // product they actually wanted. Now we go directly to /shop/:slug.
  const goToProduct = useCallback((slug: string) => {
    navigate(`/shop/${slug}`);
  }, [navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? 'bg-[#ffffff] dark:bg-black backdrop-blur-md shadow-lg border-stone-100 dark:border-white/10' : 'bg-[#ffffff] dark:bg-black/75 backdrop-blur-md border-stone-200 dark:border-white/20'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-1268" data-mp="1vejxk2xttn5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1269" data-mp="2d8wraq8vs731">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-1270" data-mp="2h9yol1j8m92d">
          <div className="flex-shrink-0 flex items-center gap-4" data-id="element-1271" data-mp="2r7wedtgqwdx3">
            <Link to="/" onClick={resetMaterials} className="relative" aria-label="206Gravel home" data-id="element-1272" data-mp="2w6phqt2biojj">
              <motion.div className="origin-left" initial={{
              opacity: 0,
              scale: 1
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              }
            }} data-id="element-1273" data-mp="30xz3vjonr9qo">
                {!LOGOS_HIDDEN && (
              /* Region-pinned wordmark: shows "253GRAVEL" / "206GRAVEL" /
              "425GRAVEL" based on the visitor's domain. No rotation —
              the navbar reads as a fixed regional identity. */
              <Logo pinned imgClassName="h-10 max-sm:h-[1.81rem] w-auto" invertOnLight tinted={navRetracted && !isOpen} data-id="element-1274" data-mp="1olafkmp9gikp" />)}
              </motion.div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8" data-id="element-1275" data-mp="1xm8kr8bfqw4v">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-1276" data-mp="1tkndc2ho7a22">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1277" data-mp="27ky3t4f2immp">
                {t('nav.shop')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1278" data-mp="d5lu5zrnnrm0" />
              </button>

              <AnimatePresence data-id="element-1279" data-mp="87kbvh21zwbl">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-1280" data-mp="88vdba810rzj">
                    <div className="grid grid-cols-3 gap-5" data-id="element-1281" data-mp="26398ed5ba9s4">
                      {categories.map(cat => <div key={cat.name} data-id="element-1282" data-mp="260whhahksp2x">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-1283" data-mp="1r71ozf1q9cdw">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-1284" data-mp="3ljhtaaayqgg4">
                            {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(prod.slug);
                      }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors text-left w-full px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1285" data-mp="3qj482o6fw4r1">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 pt-4 border-t border-stone-200 dark:border-white/10" data-id="element-1286" data-mp="1un2va4l98qye">
                      <Link to="/projects/new-gravel-driveway" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="flex items-center justify-between bg-[#059F40]/10 hover:bg-[#059F40]/20 border border-[#059F40]/30 rounded-lg p-3 transition-colors group" data-id="element-1287" data-mp="1qiv84bug3j2a">
                        <div data-id="element-1288" data-mp="3b003x6iv8c57">
                          <span className="block text-[#059F40] dark:text-[#22c95c] font-bold text-sm" data-id="element-1289" data-mp="3azqata2ixova">
                            Shop by Project
                          </span>
                          <span className="block text-stone-600 dark:text-stone-200 text-xs mt-0.5" data-id="element-1290" data-mp="3ni3vq0lffxdu">
                            Driveway, patio, drainage & more
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-1291" data-mp="11znyhlvj5b3" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown. Holds the Calculator & Quiz, Compare
             Materials, DIY Guide, Delivery Prep Guide, and Contractors
             Zone links — most of which are reference content, not
             decision tools. Labelled "Resources" so it matches the
             actual contents (and matches the mobile section header). */}
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-1292" data-mp="cxep6szzunyu">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1293" data-mp="gxyr09viifrb">
                {t('nav.resources')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1294" data-mp="qn8hxmemdsdu" />
              </button>
              <AnimatePresence data-id="element-1295" data-mp="3q4gj0eys04yc">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-2 w-56 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md rounded-xl shadow-xl border border-stone-200 dark:border-white/20 overflow-hidden" data-id="element-1296" data-mp="3q5rmses81cpg">
                    <div className="py-2" data-id="element-1297" data-mp="1jbe32a9l5je7">
                      <Link to="/help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1298" data-mp="33vsxj5vb7aq7">
                        Calculator & Quiz
                      </Link>
                      <Link to="/compare" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1299" data-mp="2p6d7xt7ilbc3">
                        {t('nav.compareMaterials')}
                      </Link>
                      <Link to="/delivery-prep" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1300" data-mp="2b6m0kbbi6t72">
                        Delivery Prep Guide
                      </Link>
                      <Link to="/blog" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1301" data-mp="9cv0zeirpb3z">
                        Blog
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* About Dropdown */}
            <div className="relative" onMouseEnter={() => setIsAboutOpen(true)} onMouseLeave={() => setIsAboutOpen(false)} data-id="element-1302" data-mp="1tx9qdvpmwc94">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1303" data-mp="2hmktvkydckqy">
                {t('nav.about')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1304" data-mp="28l4k9ffdomns" />
              </button>
              <AnimatePresence data-id="element-1305" data-mp="2cn84asg44zko">
                {isAboutOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-4 z-50 w-48" data-id="element-1306" data-mp="2cly7s7owrzg5">
                    <Link to="/about" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1307" data-mp="13mr4rlfz3aos">
                      About Us
                    </Link>
                    <Link to="/local-vs-national" onClick={() => {
                  setIsAboutOpen(false);
                  collapseCard();
                }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1308" data-mp="kpy9jjn8vzrt">
                      Why Shop Local
                    </Link>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-1309" data-mp="2qp54at50un6n">
              Contact
            </Link>

            <div className="hidden md:flex items-center space-x-6" data-id="element-1310" data-mp="30fgjjbu5oisu">
              <div className="flex items-center space-x-3" data-id="element-1311" data-mp="35drtdmkgatb2">
                <Link to="/delivery-zones" onClick={collapseCard} aria-label="Delivery Zones" title="Delivery Zones" className="relative w-9 h-9 rounded-full flex items-center justify-center text-stone-800 dark:text-white hover:text-brand-orange transition-colors group" data-id="element-1312" data-mp="35d8e6dm6qj0q">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-1313" data-mp="3jbznlmq3rgij">
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#5c5c5c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1314" data-mp="1k0yfki2epan4">
                      <circle cx="12" cy="12" r="10" fill="none" data-id="element-1315" data-mp="1hg0h72vxlshv" />
                    </svg>
                  </div>
                  <motion.div className="absolute inset-1 pointer-events-none" animate={{
                  rotate: 360
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear'
                }} style={{
                  background: isDark ? 'conic-gradient(from 0deg, rgba(255,255,255,0.5) 0deg, rgba(255,255,255,0) 90deg, transparent 360deg)' : 'conic-gradient(from 0deg, rgba(41,37,36,0.5) 0deg, rgba(41,37,36,0) 90deg, transparent 360deg)',
                  WebkitMaskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  maskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  borderRadius: '9999px'
                }} data-id="element-1316" data-mp="1co8qmscrzmrq" />
                </Link>
                <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1317" data-mp="azf0fzae95qm" />
              </div>
              <div className="h-6 w-px bg-stone-200 dark:bg-white/20" data-id="element-1318" data-mp="cgapvrt0mx44" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className={`relative flex items-center gap-2 transition-all ${totalItems > 0 ? 'pl-2.5 pr-3.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 hover:bg-stone-300/80 dark:hover:bg-white/15 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1319" data-mp="hg6qdh2i86ex">
                <div className="relative" data-id="element-1320" data-mp="3uo9ydgdlmq0d">
                  <Truck className="w-5 h-5" data-id="element-1321" data-mp="3fyluc7e19d8y" />
                  <AnimatePresence data-id="element-1322" data-mp="3fvpv8fqg8iix">
                    {totalItems > 0 && <motion.span initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1323" data-mp="3kxoyzqq3phge">
                        {totalItems}
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <AnimatePresence data-id="element-1324" data-mp="31bn4ggutqqwc">
                  {totalItems > 0 && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="text-sm font-bold whitespace-nowrap overflow-hidden" data-id="element-1325" data-mp="2wbr1mkxv55id">
                      ${totalPrice.toLocaleString()}
                    </motion.span>}
                </AnimatePresence>
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-3" data-id="element-1326" data-mp="3u0kmciv4ilok">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1327" data-mp="3p0g1kgn6n4sj" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className={`relative flex items-center gap-1.5 transition-all ${totalItems > 0 ? 'pl-2 pr-2.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1328" data-mp="107rgxlfsjkw1">
              <div className="relative" data-id="element-1329" data-mp="gk0j22hcnj1t">
                <Truck className="w-5 h-5" data-id="element-1330" data-mp="gl36u1jdl5di" />
                <AnimatePresence data-id="element-1331" data-mp="libsic3qfrji">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1332" data-mp="lj4uz0vnruvu">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </div>
              {totalItems > 0 && <span className="text-xs font-bold whitespace-nowrap" data-id="element-1333" data-mp="3rdgsud6e6nqg">
                  ${totalPrice.toLocaleString()}
                </span>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-id="element-1334" data-mp="3hezxl40f7myl">
              {isOpen ? <X className="h-6 w-6" data-id="element-1335" data-mp="38g643midzcp9" /> : <Menu className="h-6 w-6" data-id="element-1336" data-mp="38gnzyi14zown" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-1337" data-mp="33n3714eehl4b">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-1338" data-mp="2yq30ythjwid7" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-1339" data-mp="2krmw783911xp">
              <div className="flex justify-end items-center p-6" data-id="element-1340" data-mp="vm19rio6v229">
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" aria-label="Close menu" data-id="element-1341" data-mp="qo9i8prghhyl">
                  <X className="w-6 h-6" data-id="element-1342" data-mp="luoesi73fvs0" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-1343" data-mp="gvbvu8jcf8up">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-1344" data-mp="7x9m94ljn7vt">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-1345" data-mp="2xx2ispyygxg">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1346" data-mp="2jjaee9kli288">
                      <ChevronDown className="w-5 h-5" data-id="element-1347" data-mp="2ski07s5xdalf" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-1348" data-mp="1vvadwhxhexgg">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1349" data-mp="1vsej32gxkow8">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-1350" data-mp="1d1x19sxmzdpn">
                          {categories.map(cat => <div key={cat.name} data-id="element-1351" data-mp="1h38l1httx4px">
                              <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2 text-right`} data-id="element-1352" data-mp="2kfh19k4tqked">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-1353" data-mp="2u50jqoomkc8p">
                                {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(prod.slug);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-1354" data-mp="2p6z30aeo8p0e">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 pt-3 border-t border-stone-200 dark:border-white/10 space-y-2" data-id="element-1355" data-mp="3lw6l18g30fzk">
                          <Link to="/projects/new-gravel-driveway" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-[#04A05C]/10 hover:bg-[#04A05C]/20 border border-[#04A05C]/30 rounded-lg p-3 transition-colors" data-id="element-1356" data-mp="3hvcxig6jx377">
                            <div className="text-right flex-1" data-id="element-1357" data-mp="3fjluv99pf1o6">
                              <span className="block text-[#04A05C] dark:text-[#22c95c] font-bold text-sm" data-id="element-1358" data-mp="3kgugjju29nu6">
                                Shop by Project
                              </span>
                              <span className="block text-stone-500 dark:text-stone-300 text-xs mt-0.5" data-id="element-1359" data-mp="gyaqmnyuuu2j">
                                Driveway, patio, drainage & more
                              </span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c] ml-3" data-id="element-1360" data-mp="uzdbwyi100ce" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-1361" data-mp="387xphkbvcosg">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1362" data-mp="388h70zxlxb5y">
                    {t('nav.resources')}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-1363" data-mp="2tayo4e7m1s6f" />
                  </button>
                  <AnimatePresence data-id="element-1364" data-mp="3qzqn5s1vuy6i">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1365" data-mp="3lzm2rqxutjb0">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1366" data-mp="8qzsfj9lejwn">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1367" data-mp="h0osmooo2mpx">
                            Calculator & Quiz
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1368" data-mp="zzsvtgtj1m9o">
                            Compare Materials
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1369" data-mp="1iqkdr6rkykzo">
                            Delivery Prep Guide
                          </Link>
                          <Link to="/blog" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1370" data-mp="17r1gaiqgeagt">
                            Blog
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Mobile About Menu - Collapsible */}
                <div data-id="element-1371" data-mp="3sh0jyg0gtxdm">
                  <button onClick={() => setMobileAboutOpen(!mobileAboutOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1372" data-mp="3sg8lmxfhhljn">
                    About
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} data-id="element-1373" data-mp="231zl4172vdr1" />
                  </button>
                  <AnimatePresence data-id="element-1374" data-mp="1tav651hejsw7">
                    {mobileAboutOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1375" data-mp="1t7z4p36cim30">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1376" data-mp="1adbvxuj7k487">
                          <Link to="/about" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1377" data-mp="1fbdf0fgmw3jo">
                            About Us
                          </Link>
                          <Link to="/local-vs-national" onClick={() => {
                      setIsOpen(false);
                      setMobileAboutOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1378" data-mp="1sgy8gmuwpy4u">
                            Why Shop Local
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1379" data-mp="14seym2w7ldan">
                  Contact
                </Link>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1380" data-mp="2u60jbtznkorp">
                  Delivery Zones
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}